// @flow
import React from 'react';
import resourceFactory from 'app/common/lib/ResourceFactory';
import StopEventPropagation from 'app/packages/stop-event-propagation/StopEventPropagation';
import type { Viewer } from 'app/packages/pinterest-flow-types/viewer';
import withPromiseCancellation from 'app/common/lib/react/withPromiseCancellation';
import withViewer from 'app/common/lib/react/withViewer';
import { compose } from 'redux';
import { Avatar, Box, Layer, Text, Flyout, Touchable } from 'gestalt';

const MAX_RECOMMENDATIONS = 5;

const NAME_WIDTH = '220px';

const ESC_KEYCODE = 27;
const UP_KEYCODE = 38;
const DOWN_KEYCODE = 40;
const ENTER_KEYCODE = 13;

type Props = {
  addMention: Function,
  anchor: HTMLElement,
  editing: boolean,
  makeCancelablePromise: (promise: Promise<any>, uniqueKey?: string) => Promise<any>,
  query: string,
  idealDirection?: 'up' | 'right' | 'down' | 'left',
  refocus: Function,
  viewer: Viewer,
};

type State = {
  selectedIndex: number,
  isOpen: boolean,
  recommendations: Array<Object>,
  resourceLoaded: boolean,
};

export class MentionFlyout extends React.PureComponent<Props, State> {
  state: State = {
    isOpen: this.props.editing,
    recommendations: [],
    selectedIndex: 0,
    resourceLoaded: false,
  };

  componentDidMount() {
    this.mounted = true;

    this.callResource(this.props.query);
    window.addEventListener('keydown', this.handleKeyDown);
  }

  UNSAFE_componentWillReceiveProps(nextProps: Props) {
    if (nextProps.query !== this.props.query && nextProps.query !== '') {
      this.callResource(nextProps.query);
    }
  }

  componentWillUnmount() {
    window.removeEventListener('keydown', this.handleKeyDown);

    this.mounted = false;
  }

  onDismiss = () => {
    this.setState({ isOpen: false });
  };

  mounted = false;

  addMention = (user: Object) => {
    this.props.addMention(user);
    this.setState({ recommendations: [] });
  };

  handleKeyDown = (evt: Object) => {
    const handlerMap = {
      [UP_KEYCODE]: this.handlePrevMention,
      [DOWN_KEYCODE]: this.handleNextMention,
      [ESC_KEYCODE]: this.handleClose,
      [ENTER_KEYCODE]: this.handleSubmit,
    };
    const code = evt && evt.keyCode;
    if (code) {
      handlerMap[code](evt);
    }
  };

  handlePrevMention = (evt: SyntheticEvent<>) => {
    evt.preventDefault();
    if (this.state.selectedIndex > 0) {
      this.setState((prevState) => ({
        selectedIndex: prevState.selectedIndex - 1,
      }));
    }
  };

  handleNextMention = (evt: SyntheticEvent<>) => {
    evt.preventDefault();
    if (this.state.selectedIndex < this.state.recommendations.length - 1) {
      this.setState((prevState) => ({
        selectedIndex: prevState.selectedIndex + 1,
      }));
    }
  };

  handleClose = (evt: SyntheticEvent<>) => {
    evt.preventDefault();
    evt.stopPropagation();
    this.setState({ recommendations: [] });
  };

  handleSubmit = (evt: SyntheticEvent<>) => {
    evt.preventDefault();
    this.addMention(this.state.recommendations[this.state.selectedIndex]);
  };

  callResource = (query: string) => {
    const { makeCancelablePromise, viewer } = this.props;
    makeCancelablePromise(
      resourceFactory
        .create('TypeaheadResource', {
          num_boards: 0,
          count: 10,
          term: query,
          tag: 'pinners',
        })
        .callGet(),
      'typeahead',
    )
      .then((response) => {
        if (!this.mounted) {
          return;
        }

        const data = response.resource_response.data;
        this.setState({
          recommendations: data.items
            .filter((item) => item.id !== (viewer.id || null))
            .slice(0, MAX_RECOMMENDATIONS),
          resourceLoaded: true,
        });
      })
      .catch(({ isCanceled }) => {
        if (!isCanceled) {
          this.setState({ resourceLoaded: false });
        }
      });
  };

  render() {
    const { recommendations, resourceLoaded, selectedIndex } = this.state;
    const { anchor, idealDirection } = this.props;

    const idealDirectionActual = idealDirection || 'down';
    const flyoutKey = 'flyout_' + this.state.recommendations.length;
    return resourceLoaded && recommendations.length > 0 ? (
      <StopEventPropagation>
        <Layer>
          <Flyout
            key={flyoutKey}
            anchor={anchor}
            idealDirection={idealDirectionActual}
            onDismiss={this.onDismiss}
            positionRelativeToAnchor={false}
            size="md"
          >
            <Box column={12}>
              {recommendations.length > 0
                ? recommendations.map((user, index) => (
                    <div key={user.id}>
                      <Touchable onTouch={(q) => this.addMention(user)} fullWidth={false}>
                        <Box
                          display="flex"
                          alignItems="center"
                          color={index === selectedIndex ? 'lightGray' : 'transparent'}
                          overflow="hidden"
                          padding={1}
                        >
                          <Box paddingX={4}>
                            <Avatar size="md" src={user.image_medium_url} name={user.full_name} />
                          </Box>
                          <Box paddingX={4} width={NAME_WIDTH}>
                            <Text size="md" truncate weight="bold">
                              {user.full_name}
                            </Text>
                          </Box>
                        </Box>
                      </Touchable>
                    </div>
                  ))
                : null}
            </Box>
          </Flyout>
        </Layer>
      </StopEventPropagation>
    ) : null;
  }
}

export default compose(
  withPromiseCancellation,
  withViewer,
)(MentionFlyout);
